/**
 * Generated by orval 🍺
 * Do not edit manually.
 * AI Console API
 * AI 应用工程化样板 —— 接口契约的唯一来源。
 *
 * - 前端类型不由人手写，由 packages/service 从本文件生成
 * - 流式接口（SSE）不套响应包络，详见对应接口说明
 * - CI 会执行契约漂移检测：后端改了接口而契约产物没同步，构建直接失败
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import type {
  ChatStreamEventDto,
  GetChatMessages200,
  ListChatSessions200,
  ListChatSessionsParams,
  SendChatMessage200,
  SendMessageDto,
} from '../../model';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * 响应为 Server-Sent Events。每个事件的格式是 `data: {JSON}` 后跟一个空行。
 * JSON 结构见 ChatStreamEventDto：text=增量文本、done=结束、error=失败。
 * 注意：本接口**不套响应包络**，也**不会返回 HTTP 错误码** ——
 * 一旦开始吐字节，状态码就已经发出去了，改不回来。
 * 前端必须靠"流结束但没收到 done"来判定失败，而不是靠 status code。
 * 另外 Nginx 默认会缓冲代理响应，反代时必须关闭，否则流式会被攒成一坨。
 * @summary 发送消息（SSE 流式返回）
 */
export const streamChat = (
  sendMessageDto: SendMessageDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ChatStreamEventDto>> => {
  return axios.post(`/api/v1/chat/stream`, sendMessageDto, options);
};

export const getStreamChatUrl = () => {
  return axios
    .create({
      baseURL: '',
      params: null,
    })
    .getUri({
      url: `/api/v1/chat/stream`,
      baseURL: '',
    });
};

export const getStreamChatMutationKey = () => ['streamChat'] as const;

export const getStreamChatMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof streamChat>>,
    TError,
    StreamChatMutationVariables,
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof streamChat>>,
  TError,
  StreamChatMutationVariables,
  TContext
> => {
  const mutationKey = getStreamChatMutationKey();
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof streamChat>>,
    StreamChatMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return streamChat(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StreamChatMutationResult = NonNullable<Awaited<ReturnType<typeof streamChat>>>;
export type StreamChatMutationBody = SendMessageDto;
export type StreamChatMutationError = AxiosError<unknown>;
export type StreamChatMutationVariables = { data: SendMessageDto };

/**
 * @summary 发送消息（SSE 流式返回）
 */
export const useStreamChat = <TError = AxiosError<unknown>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof streamChat>>,
      TError,
      StreamChatMutationVariables,
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof streamChat>>,
  TError,
  StreamChatMutationVariables,
  TContext
> => {
  return useMutation(getStreamChatMutationOptions(options), queryClient);
};
/**
 * 与流式接口共用同一套请求参数，用于不支持 SSE 的客户端，或需要一次性拿到 token 统计的场景。
 * @summary 发送消息（一次性返回）
 */
export const sendChatMessage = (
  sendMessageDto: SendMessageDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SendChatMessage200>> => {
  return axios.post(`/api/v1/chat/messages`, sendMessageDto, options);
};

export const getSendChatMessageUrl = () => {
  return axios
    .create({
      baseURL: '',
      params: null,
    })
    .getUri({
      url: `/api/v1/chat/messages`,
      baseURL: '',
    });
};

export const getSendChatMessageMutationKey = () => ['sendChatMessage'] as const;

export const getSendChatMessageMutationOptions = <
  TError = AxiosError<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sendChatMessage>>,
    TError,
    SendChatMessageMutationVariables,
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof sendChatMessage>>,
  TError,
  SendChatMessageMutationVariables,
  TContext
> => {
  const mutationKey = getSendChatMessageMutationKey();
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof sendChatMessage>>,
    SendChatMessageMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return sendChatMessage(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SendChatMessageMutationResult = NonNullable<
  Awaited<ReturnType<typeof sendChatMessage>>
>;
export type SendChatMessageMutationBody = SendMessageDto;
export type SendChatMessageMutationError = AxiosError<unknown>;
export type SendChatMessageMutationVariables = { data: SendMessageDto };

/**
 * @summary 发送消息（一次性返回）
 */
export const useSendChatMessage = <TError = AxiosError<unknown>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof sendChatMessage>>,
      TError,
      SendChatMessageMutationVariables,
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof sendChatMessage>>,
  TError,
  SendChatMessageMutationVariables,
  TContext
> => {
  return useMutation(getSendChatMessageMutationOptions(options), queryClient);
};
/**
 * 按最近更新时间倒序，支持分页。
 * @summary 查询会话列表
 */
export const listChatSessions = (
  params?: ListChatSessionsParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ListChatSessions200>> => {
  return axios.get(`/api/v1/chat/sessions`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getListChatSessionsUrl = (params?: ListChatSessionsParams) => {
  return axios
    .create({
      baseURL: '',
      params: null,
    })
    .getUri({
      url: `/api/v1/chat/sessions`,
      baseURL: '',
      params,
    });
};

export const getListChatSessionsQueryKey = (params?: ListChatSessionsParams) => {
  return [`/api/v1/chat/sessions`, ...(params ? [params] : [])] as const;
};

export const getListChatSessionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listChatSessions>>,
  TError = AxiosError<unknown>,
>(
  params?: ListChatSessionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listChatSessions>>, TError, TData>>;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListChatSessionsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listChatSessions>>> = ({ signal }) =>
    listChatSessions(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listChatSessions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListChatSessionsQueryResult = NonNullable<Awaited<ReturnType<typeof listChatSessions>>>;
export type ListChatSessionsQueryError = AxiosError<unknown>;

export function useListChatSessions<
  TData = Awaited<ReturnType<typeof listChatSessions>>,
  TError = AxiosError<unknown>,
>(
  params: undefined | ListChatSessionsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listChatSessions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listChatSessions>>,
          TError,
          Awaited<ReturnType<typeof listChatSessions>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListChatSessions<
  TData = Awaited<ReturnType<typeof listChatSessions>>,
  TError = AxiosError<unknown>,
>(
  params?: ListChatSessionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listChatSessions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listChatSessions>>,
          TError,
          Awaited<ReturnType<typeof listChatSessions>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListChatSessions<
  TData = Awaited<ReturnType<typeof listChatSessions>>,
  TError = AxiosError<unknown>,
>(
  params?: ListChatSessionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listChatSessions>>, TError, TData>>;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary 查询会话列表
 */

export function useListChatSessions<
  TData = Awaited<ReturnType<typeof listChatSessions>>,
  TError = AxiosError<unknown>,
>(
  params?: ListChatSessionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listChatSessions>>, TError, TData>>;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListChatSessionsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary 查询指定会话的历史消息
 */
export const getChatMessages = (
  sessionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<GetChatMessages200>> => {
  return axios.get(`/api/v1/chat/sessions/${sessionId}/messages`, options);
};

export const getGetChatMessagesUrl = (sessionId: string) => {
  return axios
    .create({
      baseURL: '',
      params: null,
    })
    .getUri({
      url: `/api/v1/chat/sessions/${sessionId}/messages`,
      baseURL: '',
    });
};

export const getGetChatMessagesQueryKey = (sessionId: string) => {
  return [`/api/v1/chat/sessions/${sessionId}/messages`] as const;
};

export const getGetChatMessagesQueryOptions = <
  TData = Awaited<ReturnType<typeof getChatMessages>>,
  TError = AxiosError<unknown>,
>(
  sessionId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChatMessages>>, TError, TData>>;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetChatMessagesQueryKey(sessionId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getChatMessages>>> = ({ signal }) =>
    getChatMessages(sessionId, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: sessionId !== null && sessionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getChatMessages>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetChatMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof getChatMessages>>>;
export type GetChatMessagesQueryError = AxiosError<unknown>;

export function useGetChatMessages<
  TData = Awaited<ReturnType<typeof getChatMessages>>,
  TError = AxiosError<unknown>,
>(
  sessionId: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChatMessages>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getChatMessages>>,
          TError,
          Awaited<ReturnType<typeof getChatMessages>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetChatMessages<
  TData = Awaited<ReturnType<typeof getChatMessages>>,
  TError = AxiosError<unknown>,
>(
  sessionId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChatMessages>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getChatMessages>>,
          TError,
          Awaited<ReturnType<typeof getChatMessages>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetChatMessages<
  TData = Awaited<ReturnType<typeof getChatMessages>>,
  TError = AxiosError<unknown>,
>(
  sessionId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChatMessages>>, TError, TData>>;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary 查询指定会话的历史消息
 */

export function useGetChatMessages<
  TData = Awaited<ReturnType<typeof getChatMessages>>,
  TError = AxiosError<unknown>,
>(
  sessionId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChatMessages>>, TError, TData>>;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetChatMessagesQueryOptions(sessionId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}
